/**
 * A dashboard's own ship and range, as a `SelectionScope`.
 *
 * Explore is Explore and a dashboard is a dashboard: the toolbar edits
 * **this**, and the `selectedShip` / `startDate` / `endDate` atoms never move.
 * Every dashboard has one (`dashboard-context.ts`).
 *
 * The value lives where it is saved: the `ship` and `window` variables of the
 * document (`variables.ts`). Writing one marks the dashboard unsaved, exactly
 * like moving a tile does, and **Save** is what makes it the dashboard's ship
 * for everyone. Tiles already resolve through `resolveVariables`, so a write
 * here reaches them with nothing else to wire. */

import type { SelectionScope } from '../ship/selection-scope.ts';
import { endDate, MAX_PLOT_SHIPS, startDate, type DatePreset } from '../../state/store.ts';
import { shiftWindow, type WindowShift } from '../../utils/dates.ts';
import { dashboardDirty, dashboards, type Dashboard } from './dashboard-state.ts';
import {
  LIVE_WINDOW_VAR,
  liveWindowVariable,
  resolveWindow,
  SHIP_VAR,
  storedLiveWindow,
  WINDOW_VAR,
  type DashboardVariable,
  type TimeWindow,
} from './variables.ts';

function variable(dashboard: Dashboard, id: string): DashboardVariable | undefined {
  return dashboard.variables.find((v) => v.id === id);
}

function storedShips(dashboard: Dashboard): string[] {
  const value = variable(dashboard, SHIP_VAR)?.current ?? variable(dashboard, SHIP_VAR)?.default;
  return Array.isArray(value) ? value.filter((s) => typeof s === 'string' && s) : [];
}

function storedWindow(dashboard: Dashboard): TimeWindow | null {
  const v = variable(dashboard, WINDOW_VAR);
  const value = v?.current ?? v?.default;
  return value && !Array.isArray(value) ? (value as TimeWindow) : null;
}

/** One write: set the value, mark the dashboard unsaved, tell the screen. */
function write(dashboard: Dashboard, id: string, value: string[] | TimeWindow): void {
  // A dashboard saved before the Live window existed gets it on first use.
  if (id === LIVE_WINDOW_VAR && !variable(dashboard, id)) dashboard.variables.push(liveWindowVariable());
  const v = variable(dashboard, id);
  if (!v) return;
  v.current = value;
  dashboard.updatedAt = new Date().toISOString();
  dashboards.notify();
  dashboardDirty.set(true);
}

/**
 * The window as a concrete pair, for arithmetic and for the date inputs.
 *
 * A dashboard that has not got one yet falls back to what Explore has, which is
 * what its tiles are drawing in the meantime - so the inputs show the dates on
 * screen rather than blanks.
 */
function resolved(dashboard: Dashboard): { start: string; end: string } {
  return (
    resolveWindow(storedWindow(dashboard)) ?? { start: startDate.value, end: endDate.value }
  );
}

export function dashboardSelection(dashboard: Dashboard): SelectionScope {
  const setShips = (next: string[]): void => write(dashboard, SHIP_VAR, next);

  return {
    kind: 'dashboard',

    ships: () => storedShips(dashboard),
    anchor: () => storedShips(dashboard)[0] ?? null,
    // A dashboard is always the multi-ship case: Export is Explore's tab,
    // and it draws no tiles.
    multiAllowed: () => true,

    async pickOnly(id: string): Promise<void> {
      setShips([id]);
    },

    async addShip(id: string): Promise<void> {
      const ships = storedShips(dashboard);
      if (ships.includes(id) || ships.length >= MAX_PLOT_SHIPS) return;
      setShips([...ships, id]);
    },

    async removeShip(id: string): Promise<void> {
      // No promotion to arrange: the first of what is left is the anchor, and
      // nothing here loads columns for it.
      setShips(storedShips(dashboard).filter((s) => s !== id));
    },

    start: () => resolved(dashboard).start,
    end: () => resolved(dashboard).end,
    preset(): DatePreset {
      const preset = storedWindow(dashboard)?.preset;
      return (preset as DatePreset) ?? 'custom';
    },

    applyPreset(preset: DatePreset): void {
      // A preset is stored by name, so "Last 7 days" keeps meaning the last
      // seven days each time the dashboard is opened, rather than freezing the
      // week it was chosen in.
      write(dashboard, WINDOW_VAR, preset === 'custom' ? resolved(dashboard) : { preset });
    },

    setStart(value: string): void {
      write(dashboard, WINDOW_VAR, { start: value, end: resolved(dashboard).end });
    },

    setEnd(value: string): void {
      write(dashboard, WINDOW_VAR, { start: resolved(dashboard).start, end: value });
    },

    shift(shift: WindowShift): void {
      const moved = shiftWindow(resolved(dashboard), shift);
      if (moved) write(dashboard, WINDOW_VAR, moved);
    },

    // The document is not an atom the pickers watch, so they are told here.
    subscribe: (listener) => dashboards.subscribe(listener),
  };
}

/**
 * The window Live tiles draw, as a scope for the toolbar's second time picker
 * (`kind: 'live'`, which `<date-range>` shows with Live's short presets). It
 * edits the dashboard's `live` variable - attached or detached alike, since
 * Explore has no Live window of its own to follow - and has no ships: the
 * toolbar's ship picker is the one for every tile.
 */
export function dashboardLiveSelection(dashboard: Dashboard): SelectionScope {
  const stored = (): TimeWindow => storedLiveWindow(dashboard.variables);
  const pair = (): { start: string; end: string } =>
    resolveWindow(stored()) ?? resolveWindow({ preset: '10m' })!;
  const noShips = async (): Promise<void> => {};
  return {
    kind: 'live',
    ships: () => [],
    anchor: () => null,
    multiAllowed: () => false,
    pickOnly: noShips,
    addShip: noShips,
    removeShip: noShips,
    start: () => pair().start,
    end: () => pair().end,
    preset: (): DatePreset => (stored().preset as DatePreset) ?? 'custom',
    applyPreset(preset: DatePreset): void {
      write(dashboard, LIVE_WINDOW_VAR, preset === 'custom' ? pair() : { preset });
    },
    setStart(value: string): void {
      write(dashboard, LIVE_WINDOW_VAR, { start: value, end: pair().end });
    },
    setEnd(value: string): void {
      write(dashboard, LIVE_WINDOW_VAR, { start: pair().start, end: value });
    },
    // Live has no shifts: its windows are about now.
    shift: () => {},
    subscribe: (listener) => dashboards.subscribe(listener),
  };
}
